// ---------------------------------------------------------------------------
// OverviewPanel -- "Know Your Audience" storytelling overview
// ---------------------------------------------------------------------------
// Hero section + Demographics + Personality Snapshot + Content Footprint
// Each demographic chart has "View More" → opens Discovery with filters
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  Users,
  Eye,
  TrendingUp,
  Trophy,
  ShoppingBag,
  FileText,
  MessageCircle,
  Sparkles,
  Globe,
  MapPin,
  BarChart3,
  BadgeCheck,
  Star,
  UserCheck,
  PawPrint,
  ExternalLink,
  Tv,
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import type {
  ContentUniverseOverview,
  DemographicBucket,
  AudienceProfile,
  ChannelEngagement,
} from './types';
import type { FunnelLayerInput } from '@/services/v11/funnelService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OverviewPanelProps {
  readonly data: ContentUniverseOverview;
  readonly layers?: readonly (FunnelLayerInput & {
    dimensionLabel?: string;
    valueLabels?: readonly string[];
  })[];
  readonly people?: readonly AudienceProfile[];
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EMOTION_COLORS: Readonly<Record<string, string>> = {
  joy: '#34d399',
  excitement: '#22d3ee',
  love: '#f472b6',
  admiration: '#a78bfa',
  hope: '#60a5fa',
  pride: '#fbbf24',
  gratitude: '#2dd4bf',
  solidarity: '#10b981',
  relief: '#6ee7b7',
  nostalgia: '#818cf8',
  frustration: '#f87171',
  annoyance: '#fb923c',
  confusion: '#fbbf24',
  disappointment: '#fb7185',
  sadness: '#818cf8',
  hatred: '#ef4444',
  fear: '#f97316',
  irony: '#c084fc',
  surprise: '#e879f9',
  jealousy: '#f59e0b',
  regret: '#f472b6',
  embarrassment: '#fca5a5',
};

const BRAND_CATEGORY_COLORS: Readonly<Record<string, string>> = {
  sports: '#3b82f6',
  electronics: '#06b6d4',
  technology: '#06b6d4',
  'fashion & apparel': '#a855f7',
  fashion: '#a855f7',
  apparel: '#a855f7',
  food: '#f59e0b',
  beverages: '#f59e0b',
  automotive: '#64748b',
  entertainment: '#ec4899',
};

const DONUT_PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#f97316',
  '#6366f1',
  '#14b8a6',
];

const BAR_PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#f97316',
  '#6366f1',
  '#14b8a6',
  '#a855f7',
  '#22d3ee',
  '#f472b6',
  '#84cc16',
  '#64748b',
];

/** Map generation names → approximate age ranges (2026) */
const GENERATION_AGE_RANGES: Readonly<Record<string, string>> = {
  'gen alpha': '0–13',
  'gen z': '14–29',
  millennials: '30–45',
  'gen x': '46–61',
  boomers: '62–80',
  silent: '81+',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function OverviewPanel({ data, layers, people = [], className }: OverviewPanelProps) {
  /** Group people into follower tiers */
  const peoplByTier = useMemo(() => {
    const tiers: Record<string, AudienceProfile[]> = {
      Nano: [],
      Micro: [],
      Medio: [],
      Macro: [],
      Mega: [],
    };
    for (const p of people) {
      const f = p.followers_count;
      if (f >= 1_000_000) tiers.Mega.push(p);
      else if (f >= 500_000) tiers.Macro.push(p);
      else if (f >= 100_000) tiers.Medio.push(p);
      else if (f >= 10_000) tiers.Micro.push(p);
      else tiers.Nano.push(p);
    }
    return tiers;
  }, [people]);

  const sortedEmotions = useMemo(
    () =>
      Object.entries(data.emotions)
        .sort(([, a], [, b]) => b - a)
        .filter(([, v]) => v > 0),
    [data.emotions]
  );

  const emotionDonutData = useMemo(
    () =>
      sortedEmotions.slice(0, 8).map(([emotion, count]) => ({
        name: emotion,
        value: count,
        color: EMOTION_COLORS[emotion] ?? '#60a5fa',
      })),
    [sortedEmotions]
  );

  const totalEmotions = useMemo(
    () => sortedEmotions.reduce((sum, [, v]) => sum + v, 0),
    [sortedEmotions]
  );

  const topEmotion = sortedEmotions[0];
  const topEmotionPct =
    totalEmotions > 0 && topEmotion ? ((topEmotion[1] / totalEmotions) * 100).toFixed(0) : '0';

  const demographics = data.demographics;
  const flags = data.flags;

  return (
    <div className={cn('space-y-5', className)}>
      {/* ----------------------------------------------------------------- */}
      {/* HERO SECTION                                                       */}
      {/* ----------------------------------------------------------------- */}
      <div className="rounded-xl border border-v11-border bg-v11-surface p-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <HeroMetric
            icon={<Users className="h-5 w-5" />}
            iconColor="#3b82f6"
            label="Active Commenters"
            value={formatCompact(data.totalProfiles)}
            detail={`${data.totalProfiles.toLocaleString()} profiles`}
          />
          <HeroMetric
            icon={<Eye className="h-5 w-5" />}
            iconColor="#10b981"
            label="Total Reach"
            value={formatCompact(data.totalReach)}
            detail="combined followers"
          />
          <HeroMetric
            icon={<TrendingUp className="h-5 w-5" />}
            iconColor={getSentimentColor(data.avgSentiment)}
            label="Avg Sentiment"
            value={data.avgSentiment.toFixed(1)}
            detail={getSentimentLabel(data.avgSentiment)}
          />
          <HeroMetric
            icon={<MessageCircle className="h-5 w-5" />}
            iconColor="#8b5cf6"
            label="Total Comments"
            value={formatCompact(data.contentStats.totalComments)}
            detail={`across ${formatCompact(data.contentStats.postsCommented)} posts`}
          />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* DEMOGRAPHICS                                                       */}
      {/* ----------------------------------------------------------------- */}
      {demographics && (
        <>
          {/* Row 1: Platform (donut) + Gender (donut) + Generation (bars) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <DemographicDonut
              title="Platform"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<Globe className="h-4 w-4 text-blue-400" />}
              data={filterDash(demographics.platform)}
              total={data.totalProfiles}
            />
            <DemographicDonut
              title="Gender"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<Users className="h-4 w-4 text-pink-400" />}
              data={filterDash(demographics.gender)}
              total={data.totalProfiles}
            />
            <DemographicStackedBar
              title="Generation"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<BarChart3 className="h-4 w-4 text-purple-400" />}
              data={addGenerationAgeRanges(filterDash(demographics.generation))}
              total={data.totalProfiles}
            />
          </div>

          {/* Row 2: Country + Lifestyle (bars) + Ethnicity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <DemographicBars
              title="Country"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<MapPin className="h-4 w-4 text-emerald-400" />}
              data={filterDash(demographics.country).slice(0, 5)}
              total={data.totalProfiles}
            />
            <DemographicBars
              title="Lifestyle"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<Star className="h-4 w-4 text-pink-400" />}
              data={filterDash(demographics.lifestyle).slice(0, 5)}
              total={data.totalProfiles}
            />
            <DemographicStackedBar
              title="Ethnicity"
              subtitle={`Based on ${data.totalProfiles.toLocaleString()} profiles`}
              icon={<Users className="h-4 w-4 text-amber-400" />}
              data={filterDash(demographics.ethnicity).slice(0, 6)}
              total={data.totalProfiles}
            />
          </div>

          {/* Follower Tier — full width with profile photos */}
          <FollowerTierShowcase
            tiers={filterDash(demographics.followerTier)}
            total={data.totalProfiles}
            peopleByTier={peoplByTier}
          />
        </>
      )}

      {/* ----------------------------------------------------------------- */}
      {/* CHANNEL ENGAGEMENT (hidden for now)                                */}
      {/* ----------------------------------------------------------------- */}
      {/* {data.channelDistribution && data.channelDistribution.length > 0 && (
        <ChannelDistributionCard
          channels={data.channelDistribution}
          totalProfiles={data.totalProfiles}
        />
      )} */}

      {/* ----------------------------------------------------------------- */}
      {/* PERSONALITY SNAPSHOT                                                */}
      {/* ----------------------------------------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Emotion Profile — Donut Chart */}
        <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="h-4 w-4 text-purple-400" />
            <span className="text-sm font-semibold text-v11-text-primary">Emotion Profile</span>
          </div>
          <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
            Based on {data.totalProfiles.toLocaleString()} profiles
          </p>

          <div className="flex items-center gap-4">
            <div className="w-[120px] h-[120px] flex-shrink-0 relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={emotionDonutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={55}
                    paddingAngle={2}
                    dataKey="value"
                    stroke="none"
                  >
                    {emotionDonutData.map(entry => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold text-v11-text-primary">{topEmotionPct}%</span>
                <span className="text-[9px] text-v11-text-muted capitalize">{topEmotion?.[0]}</span>
              </div>
            </div>

            <div className="flex-1 space-y-1.5">
              {sortedEmotions.slice(0, 5).map(([emotion, count]) => {
                const pct = totalEmotions > 0 ? ((count / totalEmotions) * 100).toFixed(0) : '0';
                const color = EMOTION_COLORS[emotion] ?? '#60a5fa';
                return (
                  <div key={emotion} className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-v11-text-secondary capitalize flex-1 truncate">
                      {emotion}
                    </span>
                    <span className="text-xs text-v11-text-muted tabular-nums">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Brand Affinity */}
        <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingBag className="h-4 w-4 text-orange-400" />
            <span className="text-sm font-semibold text-v11-text-primary">Brand Affinity</span>
          </div>
          <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
            Based on {data.totalProfiles.toLocaleString()} profiles
          </p>
          <BrandList
            items={data.topBrands.slice(0, 5)}
            totalProfiles={data.totalProfiles}
            barColor="#f97316"
          />
        </div>

        {/* Team Loyalty */}
        <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-4 w-4 text-emerald-400" />
            <span className="text-sm font-semibold text-v11-text-primary">Team Loyalty</span>
          </div>
          <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
            Based on {data.totalProfiles.toLocaleString()} profiles
          </p>
          <TeamList
            items={data.topTeams.slice(0, 5)}
            totalProfiles={data.totalProfiles}
            barColor="#10b981"
          />
        </div>
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* CONTENT FOOTPRINT                                                  */}
      {/* ----------------------------------------------------------------- */}
      <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="h-4 w-4 text-cyan-400" />
          <span className="text-sm font-semibold text-v11-text-primary">Content Footprint</span>
        </div>
        <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
          Based on {data.totalProfiles.toLocaleString()} profiles
        </p>
        <div className="grid grid-cols-3 gap-6">
          <FootprintStat
            value={formatCompact(data.contentStats.postsCommented)}
            label="Posts Commented"
            color="#06b6d4"
          />
          <FootprintStat
            value={formatCompact(data.contentStats.totalComments)}
            label="Total Comments"
            color="#8b5cf6"
          />
          <FootprintStat
            value={formatCompact(data.contentStats.activeCommenters)}
            label="Active Commenters"
            color="#3b82f6"
          />
        </div>

        <div className="mt-4 pt-4 border-t border-v11-border/50">
          <div className="flex h-3 rounded-full overflow-hidden bg-v11-bg">
            {sortedEmotions.slice(0, 8).map(([emotion, count]) => {
              const pct = totalEmotions > 0 ? (count / totalEmotions) * 100 : 0;
              return (
                <div
                  key={emotion}
                  className="h-full transition-all"
                  style={{
                    width: `${pct}%`,
                    backgroundColor: EMOTION_COLORS[emotion] ?? '#60a5fa',
                  }}
                  title={`${emotion}: ${pct.toFixed(1)}%`}
                />
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {sortedEmotions.slice(0, 6).map(([emotion]) => (
              <div key={emotion} className="flex items-center gap-1">
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: EMOTION_COLORS[emotion] ?? '#60a5fa' }}
                />
                <span className="text-[10px] text-v11-text-muted capitalize">{emotion}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemographicDonut — Mini donut chart for distributions with few buckets
// ---------------------------------------------------------------------------

function DemographicDonut({
  title,
  subtitle,
  icon,
  data,
  total,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon: React.ReactNode;
  readonly data: readonly DemographicBucket[];
  readonly total: number;
}) {
  const donutData = data.slice(0, 8).map((bucket, i) => ({
    name: bucket.name,
    value: bucket.count,
    color: DONUT_PALETTE[i % DONUT_PALETTE.length],
  }));

  const topItem = data[0];
  const topPct = topItem && total > 0 ? ((topItem.count / total) * 100).toFixed(0) : '0';

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-v11-text-primary">{title}</span>
      </div>
      {subtitle && <p className="text-[11px] text-v11-text-muted mb-4 ml-6">{subtitle}</p>}

      <div className="flex items-center gap-4">
        <div className="w-[100px] h-[100px] flex-shrink-0 relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius={28}
                outerRadius={45}
                paddingAngle={2}
                dataKey="value"
                stroke="none"
              >
                {donutData.map(entry => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* Center number */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-sm font-bold text-v11-text-primary">{topPct}%</span>
            <span className="text-[8px] text-v11-text-muted capitalize truncate max-w-[50px] text-center">
              {topItem?.name}
            </span>
          </div>
        </div>

        <div className="flex-1 space-y-1.5">
          {data.slice(0, 6).map((bucket, i) => {
            const pct = total > 0 ? ((bucket.count / total) * 100).toFixed(1) : '0';
            return (
              <div key={bucket.name} className="flex items-center gap-2">
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: DONUT_PALETTE[i % DONUT_PALETTE.length] }}
                />
                <span className="text-xs text-v11-text-secondary flex-1 truncate">
                  {bucket.name}
                </span>
                <span className="text-xs text-v11-text-muted tabular-nums">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FollowerTierShowcase — Full-width card with profile photos per tier
// ---------------------------------------------------------------------------

const TIER_CONFIG: readonly {
  name: string;
  label: string;
  range: string;
  color: string;
  icon: string;
}[] = [
  { name: 'Mega', label: 'Mega', range: '1M+', color: '#ef4444', icon: '🔥' },
  { name: 'Macro', label: 'Macro', range: '500K–1M', color: '#f59e0b', icon: '⭐' },
  { name: 'Medio', label: 'Medio', range: '100K–500K', color: '#10b981', icon: '📈' },
  { name: 'Micro', label: 'Micro', range: '10K–100K', color: '#3b82f6', icon: '💎' },
  { name: 'Nano', label: 'Nano', range: '<10K', color: '#8b5cf6', icon: '🌱' },
];

function FollowerTierShowcase({
  tiers,
  total,
  peopleByTier,
}: {
  readonly tiers: readonly DemographicBucket[];
  readonly total: number;
  readonly peopleByTier: Record<string, AudienceProfile[]>;
}) {
  const tierMap = new Map(tiers.map(t => [t.name, t.count]));
  // Circle sizes from largest (Mega) to smallest (Nano)
  const circleSizes = [72, 60, 48, 38, 30];

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="h-4 w-4 text-orange-400" />
        <span className="text-sm font-semibold text-v11-text-primary">Follower Tier</span>
      </div>
      <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
        Based on {total.toLocaleString()} profiles
      </p>

      {/* Chips row */}
      <div className="flex items-center gap-2 flex-wrap mb-6">
        {TIER_CONFIG.map(tier => {
          const count = tierMap.get(tier.name) ?? 0;
          if (count === 0) return null;
          const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
          return (
            <div
              key={tier.name}
              className="flex items-center gap-1.5 rounded-full px-3 py-1"
              style={{
                backgroundColor: `${tier.color}18`,
                borderWidth: 1,
                borderColor: `${tier.color}40`,
              }}
            >
              <span className="text-xs">{tier.icon}</span>
              <span className="text-xs font-semibold" style={{ color: tier.color }}>
                {tier.label}
              </span>
              <span className="text-[10px] text-v11-text-muted tabular-nums">
                {formatCompact(count)}
              </span>
              <span className="text-[10px] font-medium tabular-nums" style={{ color: tier.color }}>
                {pct}%
              </span>
            </div>
          );
        })}
      </div>

      {/* Visual scale: line with decreasing circles */}
      <div className="relative flex items-center justify-between px-4">
        {/* Connecting line */}
        <div className="absolute left-4 right-4 top-1/2 h-px bg-v11-border" />

        {TIER_CONFIG.map((tier, idx) => {
          const count = tierMap.get(tier.name) ?? 0;
          if (count === 0) return null;
          const profiles = peopleByTier[tier.name] ?? [];
          const size = circleSizes[idx] ?? 28;
          const topProfile = profiles[0];

          return (
            <div
              key={tier.name}
              className="relative flex flex-col items-center z-10 group/tier"
              style={{ width: size + 24 }}
            >
              {/* Circle with photo or fallback */}
              <div
                className="rounded-full overflow-hidden flex-shrink-0 shadow-lg cursor-pointer transition-transform group-hover/tier:scale-110"
                style={{
                  width: size,
                  height: size,
                  borderWidth: 3,
                  borderColor: tier.color,
                  backgroundColor: `${tier.color}20`,
                }}
              >
                {topProfile?.profile_pic_url ? (
                  <img
                    src={topProfile.profile_pic_url}
                    alt={topProfile.username}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center font-bold text-white"
                    style={{ backgroundColor: tier.color, fontSize: size * 0.35 }}
                  >
                    {tier.label[0]}
                  </div>
                )}
              </div>

              {/* Tooltip on hover */}
              {topProfile && (
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 opacity-0 pointer-events-none group-hover/tier:opacity-100 group-hover/tier:pointer-events-auto transition-opacity duration-200 z-50">
                  <div className="bg-v11-bg border border-v11-border rounded-lg shadow-xl px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-v11-text-primary">
                        @{topProfile.username}
                      </span>
                      {topProfile.platform_name &&
                        (() => {
                          const pi = getPlatformIcon(topProfile.platform_name);
                          return (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"
                              style={{
                                backgroundColor: `${pi?.color ?? tier.color}20`,
                                color: pi?.color ?? tier.color,
                              }}
                            >
                              {pi && <span className="[&>svg]:h-2.5 [&>svg]:w-2.5">{pi.icon}</span>}
                              {topProfile.platform_name}
                            </span>
                          );
                        })()}
                    </div>
                    <div className="text-[11px] text-v11-text-muted tabular-nums">
                      {formatCompact(topProfile.followers_count)} followers
                    </div>
                    {/* Arrow */}
                    <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 rotate-45 bg-v11-bg border-r border-b border-v11-border" />
                  </div>
                </div>
              )}

              {/* Label below */}
              <span className="text-[10px] font-semibold mt-1.5" style={{ color: tier.color }}>
                {tier.label}
              </span>
              <span className="text-[9px] text-v11-text-muted">{tier.range}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ChannelDistributionCard — Where does this audience engage?
// ---------------------------------------------------------------------------

const NETWORK_COLORS: Readonly<Record<string, string>> = {
  Instagram: '#E1306C',
  TikTok: '#00f2ea',
  X: '#1DA1F2',
  YouTube: '#FF0000',
};

function ChannelDistributionCard({
  channels,
  totalProfiles,
}: {
  readonly channels: readonly ChannelEngagement[];
  readonly totalProfiles: number;
}) {
  const maxCount = channels[0]?.profiles_count ?? 1;

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-1">
        <Tv className="h-4 w-4 text-cyan-400" />
        <span className="text-sm font-semibold text-v11-text-primary">Channel Engagement</span>
      </div>
      <p className="text-[11px] text-v11-text-muted mb-4 ml-6">
        Based on {totalProfiles.toLocaleString()} profiles
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {channels.slice(0, 12).map(ch => {
          const pct =
            totalProfiles > 0 ? ((ch.profiles_count / totalProfiles) * 100).toFixed(1) : '0';
          const barWidth = maxCount > 0 ? (ch.profiles_count / maxCount) * 100 : 0;

          return (
            <div key={ch.datasource_id}>
              {/* Channel name + network icons + stats */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Network icons */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {ch.networks.map(net => {
                      const pi = getPlatformIcon(net);
                      return pi ? (
                        <span
                          key={net}
                          className="flex-shrink-0 [&>svg]:h-3.5 [&>svg]:w-3.5"
                          style={{ color: pi.color }}
                          title={pi.name}
                        >
                          {pi.icon}
                        </span>
                      ) : (
                        <span
                          key={net}
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: NETWORK_COLORS[net] ?? '#64748b' }}
                          title={net}
                        />
                      );
                    })}
                  </div>
                  <span className="text-xs text-v11-text-primary truncate font-medium">
                    {ch.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
                  <span className="text-xs font-semibold text-v11-text-secondary tabular-nums w-12 text-right">
                    {formatCompact(ch.profiles_count)}
                  </span>
                </div>
              </div>
              {/* Bar */}
              <div className="h-1.5 bg-v11-bg rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${barWidth}%`,
                    background: 'linear-gradient(90deg, #06b6d4, #06b6d480)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemographicStackedBar — Single segmented bar with legend
// ---------------------------------------------------------------------------

function DemographicStackedBar({
  title,
  subtitle,
  icon,
  data,
  total,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon: React.ReactNode;
  readonly data: readonly DemographicBucket[];
  readonly total: number;
}) {
  const dataTotal = data.reduce((sum, b) => sum + b.count, 0);

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-v11-text-primary">{title}</span>
      </div>
      {subtitle && <p className="text-[11px] text-v11-text-muted mb-4 ml-6">{subtitle}</p>}

      {/* Stacked bar */}
      <div className="flex h-6 rounded-lg overflow-hidden bg-v11-bg">
        {data.map((bucket, i) => {
          const segPct = dataTotal > 0 ? (bucket.count / dataTotal) * 100 : 0;
          if (segPct < 0.5) return null;
          return (
            <div
              key={bucket.name}
              className="h-full transition-all relative group"
              style={{
                width: `${segPct}%`,
                backgroundColor: BAR_PALETTE[i % BAR_PALETTE.length],
              }}
              title={`${bucket.name}: ${segPct.toFixed(1)}%`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 space-y-1.5">
        {data.map((bucket, i) => {
          const pct = total > 0 ? ((bucket.count / total) * 100).toFixed(1) : '0';
          return (
            <div key={bucket.name} className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: BAR_PALETTE[i % BAR_PALETTE.length] }}
              />
              <span className="text-xs text-v11-text-secondary flex-1 truncate">{bucket.name}</span>
              <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
              <span className="text-xs font-semibold text-v11-text-secondary tabular-nums w-12 text-right">
                {formatCompact(bucket.count)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemographicPills — Proportional colored pills/tags
// ---------------------------------------------------------------------------

function DemographicPills({
  title,
  icon,
  data,
  total,
}: {
  readonly title: string;
  readonly icon: React.ReactNode;
  readonly data: readonly DemographicBucket[];
  readonly total: number;
}) {
  const maxCount = data[0]?.count ?? 1;

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <span className="text-sm font-semibold text-v11-text-primary">{title}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {data.map((bucket, i) => {
          const pct = total > 0 ? ((bucket.count / total) * 100).toFixed(1) : '0';
          const intensity = maxCount > 0 ? Math.max(0.15, bucket.count / maxCount) : 0.15;
          const color = BAR_PALETTE[i % BAR_PALETTE.length];

          return (
            <div
              key={bucket.name}
              className="rounded-full px-3 py-1.5 flex items-center gap-2 transition-all"
              style={{
                backgroundColor: `${color}${Math.round(intensity * 40)
                  .toString(16)
                  .padStart(2, '0')}`,
                borderWidth: 1,
                borderColor: `${color}40`,
              }}
            >
              <span className="text-xs font-medium" style={{ color }}>
                {bucket.name}
              </span>
              <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
            </div>
          );
        })}
      </div>

      {/* Compact legend with counts */}
      <div className="mt-3 pt-3 border-t border-v11-border/50 space-y-1">
        {data.map((bucket, i) => (
          <div key={bucket.name} className="flex items-center justify-between">
            <span className="text-[11px] text-v11-text-muted truncate">{bucket.name}</span>
            <span className="text-xs font-semibold text-v11-text-secondary tabular-nums">
              {formatCompact(bucket.count)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DemographicBars — Horizontal bar chart for distributions
// ---------------------------------------------------------------------------

function DemographicBars({
  title,
  subtitle,
  icon,
  data,
  total,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly icon: React.ReactNode;
  readonly data: readonly DemographicBucket[];
  readonly total: number;
}) {
  const maxCount = data[0]?.count ?? 1;

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-semibold text-v11-text-primary">{title}</span>
      </div>
      {subtitle && <p className="text-[11px] text-v11-text-muted mb-4 ml-6">{subtitle}</p>}

      <div className="space-y-2">
        {data.map((bucket, i) => {
          const pct = total > 0 ? ((bucket.count / total) * 100).toFixed(1) : '0';
          const barWidth = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
          const color = BAR_PALETTE[i % BAR_PALETTE.length];

          return (
            <div key={bucket.name}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs text-v11-text-secondary truncate flex-1 mr-2">
                  {bucket.name}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-v11-text-muted tabular-nums">{pct}%</span>
                  <span className="text-xs font-semibold text-v11-text-secondary tabular-nums w-12 text-right">
                    {formatCompact(bucket.count)}
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-v11-bg rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${barWidth}%`,
                    background: `linear-gradient(90deg, ${color}, ${color}80)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlagRow — single flag stat
// ---------------------------------------------------------------------------

function FlagRow({
  icon,
  label,
  stat,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly stat: { readonly count: number; readonly pct: number };
}) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-xs text-v11-text-secondary flex-1">{label}</span>
      <span className="text-xs font-semibold text-v11-text-primary tabular-nums">
        {formatCompact(stat.count)}
      </span>
      <span className="text-[10px] text-v11-text-muted tabular-nums w-10 text-right">
        {stat.pct}%
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeroMetric
// ---------------------------------------------------------------------------

function HeroMetric({
  icon,
  iconColor,
  label,
  value,
  detail,
}: {
  readonly icon: React.ReactNode;
  readonly iconColor: string;
  readonly label: string;
  readonly value: string;
  readonly detail: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 mb-1">
        <span style={{ color: iconColor }}>{icon}</span>
        <span className="text-[11px] font-medium text-v11-text-muted uppercase tracking-wide">
          {label}
        </span>
      </div>
      <span className="text-3xl font-bold text-v11-text-primary tabular-nums leading-none">
        {value}
      </span>
      <span className="text-xs text-v11-text-muted">{detail}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BrandList
// ---------------------------------------------------------------------------

function BrandList({
  items,
  totalProfiles,
  barColor,
}: {
  readonly items: readonly {
    brand_id: number;
    brand_name: string;
    brand_category_name: string | null;
    profiles_count: number;
  }[];
  readonly totalProfiles: number;
  readonly barColor: string;
}) {
  const maxCount = items[0]?.profiles_count ?? 1;

  return (
    <div className="space-y-2.5">
      {items.map((brand, i) => {
        const pct =
          totalProfiles > 0 ? ((brand.profiles_count / totalProfiles) * 100).toFixed(1) : '0';
        const barWidth = maxCount > 0 ? (brand.profiles_count / maxCount) * 100 : 0;
        const catColor = brand.brand_category_name
          ? (BRAND_CATEGORY_COLORS[brand.brand_category_name.toLowerCase()] ?? '#64748b')
          : '#64748b';

        return (
          <div key={brand.brand_id}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-v11-text-muted w-4 text-right font-semibold">
                  {i + 1}
                </span>
                <span className="text-sm text-v11-text-primary truncate font-medium">
                  {brand.brand_name}
                </span>
                {brand.brand_category_name && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: `${catColor}20`, color: catColor }}
                  >
                    {brand.brand_category_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <span className="text-xs text-v11-text-muted tabular-nums">{pct}%</span>
                <span className="text-xs font-semibold text-v11-text-secondary tabular-nums">
                  {brand.profiles_count.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="ml-6 h-1.5 bg-v11-bg rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${barWidth}%`,
                  background: `linear-gradient(90deg, ${barColor}, ${barColor}99)`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TeamList
// ---------------------------------------------------------------------------

function TeamList({
  items,
  totalProfiles,
  barColor,
}: {
  readonly items: readonly {
    entity_id: number;
    team_name: string;
    type: string;
    team_country_name: string | null;
    profiles_count: number;
  }[];
  readonly totalProfiles: number;
  readonly barColor: string;
}) {
  const maxCount = items[0]?.profiles_count ?? 1;

  return (
    <div className="space-y-2.5">
      {items.map((team, i) => {
        const pct =
          totalProfiles > 0 ? ((team.profiles_count / totalProfiles) * 100).toFixed(1) : '0';
        const barWidth = maxCount > 0 ? (team.profiles_count / maxCount) * 100 : 0;

        return (
          <div key={team.entity_id}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-v11-text-muted w-4 text-right font-semibold">
                  {i + 1}
                </span>
                <span className="text-sm text-v11-text-primary truncate font-medium">
                  {team.team_name}
                </span>
                {team.team_country_name && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-v11-bg text-v11-text-muted font-medium">
                    {team.team_country_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <span className="text-xs text-v11-text-muted tabular-nums">{pct}%</span>
                <span className="text-xs font-semibold text-v11-text-secondary tabular-nums">
                  {team.profiles_count.toLocaleString()}
                </span>
              </div>
            </div>
            <div className="ml-6 h-1.5 bg-v11-bg rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${barWidth}%`,
                  background: `linear-gradient(90deg, ${barColor}, ${barColor}99)`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FootprintStat
// ---------------------------------------------------------------------------

function FootprintStat({
  value,
  label,
  color,
}: {
  readonly value: string;
  readonly label: string;
  readonly color: string;
}) {
  return (
    <div className="text-center">
      <div className="text-2xl font-bold tabular-nums" style={{ color }}>
        {value}
      </div>
      <div className="text-xs text-v11-text-muted mt-0.5">{label}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Append age range to generation names, e.g. "Millennials" → "Millennials (30–45)" */
function addGenerationAgeRanges(data: readonly DemographicBucket[]): readonly DemographicBucket[] {
  return data.map(bucket => {
    const range = GENERATION_AGE_RANGES[bucket.name.toLowerCase()];
    return range ? { ...bucket, name: `${bucket.name} (${range})` } : bucket;
  });
}

/** Filter out '—' (dash) entries from demographic data */
function filterDash(data: readonly DemographicBucket[]): readonly DemographicBucket[] {
  return data.filter(d => d.name !== '—' && d.name !== '' && d.name !== 'Unknown');
}

function formatCompact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function getSentimentLabel(score: number): string {
  if (score >= 7) return 'Positive';
  if (score >= 4) return 'Neutral';
  return 'Negative';
}

function getSentimentColor(score: number): string {
  if (score >= 7) return '#34d399';
  if (score >= 4) return '#fbbf24';
  return '#f87171';
}

export { OverviewPanel };
export type { OverviewPanelProps };
